import React, { useState } from "react";
import { FromWrapper } from "../styled";
import { Flex } from "../../../components/styled";
import { Field, Form } from "react-final-form";
import BITextField from "../../../components/common/TextField";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";

import {
  required,
  maxLength,
  composeValidators,
  companyName,
  validateNIT,
  validateEmail,
  mustBeNumber,
  noSpecialChars,
  validateDPI,
} from "../../../utils/validators";
import {
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import BISelect from "../../../components/common/Select";
import {
  entityTypes,
  providerType,
  typeCompany,
} from "../../../utils/constants";
import BIButton from "../../../components/common/Button";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSnackbar } from "../../../context/redux/reducers/snackbar";
import { GppBadOutlined, Close, Info } from "@mui/icons-material";
import { setDialog } from "../../../context/redux/reducers/dialog";
import { colors } from "../../../theme/colors";
import dayjs from "dayjs";

const InfoModal = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "35px",
          padding: "1rem",
          position: "relative",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: colors.gray.textLight,
        }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ padding: "2rem", textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "700",
            fontSize: "32px",
            color: "#2B4C6F",
            marginBottom: "1.5rem",
            lineHeight: "2rem",
          }}
        >
          ¿Por qué pedimos
          <br />
          esta información?
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: "20px",
            lineHeight: "27px",
            color: colors.primary,
            textAlign: "left",
            marginBottom: "2rem",
          }}
        >
          Para brindarte una experiencia segura y personalizada, usamos tu DPI y
          fecha de nacimiento como credenciales de acceso. Estos datos son
          únicos e inalterables, lo que garantiza tu seguridad. Además, nuestra
          plataforma no maneja información sensible, por lo que no hay riesgos
          asociados.
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.yellow.secondary,
            border: "1px solid #FFDB99",
            borderRadius: "15px",
            padding: "1rem",
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
          }}
        >
          <Info
            sx={{
              color: colors.primary,
              fontSize: "20px",
              marginTop: "1.5rem",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#856404",
              lineHeight: "19px",
              textAlign: "left",
            }}
          >
            Queremos recordarte que esta plataforma está disponible para todo el
            público, sin importar si eres cliente de Banco Industrial o no.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default function CreateProviderForm() {
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (formProps) => {
    setLoading(true);

    try {
      const { name, phone, dpi, email, birthDate } = formProps;

      const response = await fetch(
        `${process.env.REACT_APP_BE_URL}/rtm/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            phone,
            dpi,
            email,
            birthDate,
          }),
        },
      );

      const res = await response.json();

      if (
        res.error === true ||
        (res.result && res.result[0] && res.result[0].EstadoRegistro === 0)
      ) {
        const errorMessage = res.result[0]?.Mensaje || "Error desconocido";
        throw new Error(errorMessage);
      }

      if (res.result && res.result[0] && res.result[0].EstadoRegistro === 1) {
        const content = (
          <>
            <DialogContentText textAlign="center">
              Se notificará por medio de correo electrónico tu usuario y
              contraseña.
            </DialogContentText>
            <DialogContentText
              color="primary"
              fontWeight="bold"
              textAlign="center"
              marginTop={"2rem"}
            >
              Consultas escribe a: <br /> retosbi@bi.com.gt
            </DialogContentText>
            <DialogActions>
              <BIButton
                variant="contained"
                color="primary"
                style={{
                  width: "75%",
                  margin: "1.5rem auto 0 auto",
                }}
                onClick={() => {
                  dispatch(setDialog(false, "sm", "", ``, "paper"));
                  navigate("../");
                }}
              >
                Cerrar
              </BIButton>
            </DialogActions>
          </>
        );

        dispatch(
          setDialog(
            true,
            "sm",
            `Tu solicitud ha sido enviada exitosamente.`,
            content,
            "paper",
          ),
        );
      } else {
        throw new Error("Respuesta inesperada del servidor");
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          err.message || "Error desconocido",
          <GppBadOutlined />,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FromWrapper>
      <Form
        onSubmit={handleSubmit}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
      >
        {(formProps) => (
          <form
            noValidate
            autoComplete="none"
            onSubmit={formProps.handleSubmit}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: "700", fontSize: "24px" }}
            >
              Bienvenido a Retos Bi en Línea
            </Typography>

            <Box sx={{ padding: "0 6.5rem" }}>
              <Typography
                variant="body1"
                marginTop="1rem"
                sx={{
                  fontSize: "1rem",
                  color: colors.gray.textLight,
                  textAlign: "left",
                }}
              >
                Hemos visto que aún no eres cliente de Banco Industrial, para
                continuar, llena la siguiente información:
              </Typography>
              <Field
                name="name"
                component={BITextField}
                variant="standard"
                required
                validate={composeValidators(
                  required,
                  noSpecialChars,
                  maxLength("", 50),
                )}
                placeholder="Nombre completo"
                sx={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.primary,
                    fontSize: "14px",
                    opacity: 1,
                  },
                }}
              />
              <Field
                name="phone"
                component={BITextField}
                variant="standard"
                required
                validate={composeValidators(
                  required,
                  mustBeNumber,
                  maxLength("", 50),
                )}
                placeholder="Teléfono"
                sx={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.primary,
                    fontSize: "14px",
                    opacity: 1,
                  },
                }}
              />
              <Field
                name="dpi"
                component={BITextField}
                variant="standard"
                required
                validate={composeValidators(
                  required,
                  validateDPI,
                  maxLength("", 50),
                )}
                placeholder="DPI"
                sx={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.primary,
                    fontSize: "14px",
                    opacity: 1,
                  },
                }}
              />
              <Field name="birthDate" validate={composeValidators(required)}>
                {({ input }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateField
                      value={input.value ? dayjs(input.value) : null}
                      onChange={(newValue) => {
                        const formattedDate = newValue
                          ? dayjs(newValue).format("YYYY-MM-DD")
                          : null;
                        input.onChange(formattedDate);
                      }}
                      variant="standard"
                      placeholder="Fecha de nacimiento"
                      format="DD/MM/YYYY"
                      sx={{
                        width: "100%",
                        marginTop: "1rem",
                        "& .MuiInputBase-input::placeholder": {
                          color: colors.primary,
                          fontSize: "14px",
                          opacity: 1,
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </Field>

              <Field
                name="email"
                component={BITextField}
                variant="standard"
                required
                validate={composeValidators(
                  required,
                  validateEmail(),
                  maxLength("", 50),
                )}
                placeholder="Correo electrónico"
                sx={{
                  marginTop: "1.5rem",
                  marginBottom: "1rem",
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.primary,
                    fontSize: "14px",
                    opacity: 1,
                  },
                }}
              />

              <BIButton
                variant="contained"
                color="primary"
                type="submit"
                loading={loading}
                disabled={loading}
                id="Btn-bienvenido-continuar-retos"
                style={{
                  marginTop: "2rem",
                  width: "100%",
                }}
              >
                Continuar
              </BIButton>

              <Typography
                variant="body1"
                marginTop="2rem"
                sx={{ fontSize: "1rem" }}
              >
                <Link
                  id="Btn-bienvenido-cancelar"
                  onClick={() => navigate("/")}
                  underline="always"
                  sx={{
                    color: colors.secondary,
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </Link>
              </Typography>

              <Typography
                variant="body1"
                marginTop="1rem"
                sx={{ fontSize: "1rem" }}
              >
                ¿Por qué te pedimos esta información?
                <br />
                <Link
                  onClick={() => setShowInfoModal(true)}
                  underline="always"
                  sx={{
                    color: colors.secondary,
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  Entérate aquí
                </Link>
              </Typography>
            </Box>
          </form>
        )}
      </Form>
      {/* Modal de información */}
      <InfoModal open={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </FromWrapper>
  );
}
